import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import accepts from 'attr-accept';
import toArray from 'lodash/toArray';
import SimpleModal from '@adsk/bim360-matrix-react-simple-modal';
import SvgIcon from '@adsk/bim360-matrix-react-svg-icon';
import DocumentSelectionTree from '@adsk/bim360-matrix-react-document-selection-tree';
import Button from '@adsk/bim360-matrix-react-button';
import SaveButton from '@adsk/bim360-matrix-react-save-button';
import { MDText } from 'i18n-react';
import uploadFromComputerIcon from '@adsk/bim360-matrix-react-assets/upload-icon.svg';
import { withLocale, TSymbolProp } from '@adsk/bim360-matrix-react-helpers/es/locale';

import './_DocumentSelectionModal.scss';

/**
 * Document select modal component
 */
export class DocumentSelectionModal extends Component {
  /**
   * DocumentSelectionModal component constructor.
   * @param props
   */
  constructor(props) {
    super(props);

    this.state = this.getInitialState();

    this.state = {
      selectedDocuments: [],
      isSaving: props.isSaving
    };

    this._handleDocumentsSelect = this._handleDocumentsSelect.bind(this);
    this._handleSaveClick = this._handleSaveClick.bind(this);
    this.onFinish = this.onFinish.bind(this);
  }

  /**
   * componentWillReceiveProps
   * @param nextProps
   */
  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.isModalOpen && !this.props.isModalOpen) {
      this.setState(this.getInitialState());
    }
    if (nextProps.isSaving !== this.props.isSaving && nextProps.isSaving !== this.state.isSaving) {
      this.setState({ isSaving: nextProps.isSaving });
    }
  }

  onFinish() {
    this.mounted && this.setState({ isSaving: false });
  }

  componentDidMount() {
    this.mounted = true;
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  /**
   * Get initial state
   */
  getInitialState() {
    return {
      selectedDocuments: [],
      isSaving: false
    };
  }

  /**
   * _handleDocumentsSelect
   * @param docs {array | string}
   */
  _handleDocumentsSelect(docs) {
    const selectedDocuments = Array.isArray(docs) ? docs : [docs];
    this.setState({ selectedDocuments });
  }

  /**
   * open the input field
   */
  _handleUploadFromComputerClick() {
    this.fileInput.click();
  }

  /**
   * convert files from object of object to array of objects
   */
  _handleUploadFiles() {
    if (this.fileInput.files.length > 0) {
      const filesArray = toArray(this.fileInput.files);
      if (!filesArray.find(file => !accepts(file, this.props.acceptFileTypes))) {
        this.props.uploadFiles(Array.prototype.slice.call(this.fileInput.files));
      }
    }
  }

  /**
   * Get accept text ("Only PNG, JPG")
   */
  getUploadAcceptText() {
    const { acceptFileTypes } = this.props;
    if (!acceptFileTypes) {
      return '';
    }
    return this.props[TSymbolProp].translate('ACCEPTS_TYPES', {
      types: acceptFileTypes.replace(/\./g, '')
    });
  }

  renderSelectedCount() {
    const { selectedDocuments } = this.state;
    const documentsCount = selectedDocuments ? selectedDocuments.length : 0;
    const titleTextForDocumentsSelectedCount = this.props[
      TSymbolProp
    ].translate('DOCUMENT_SELECTED_COUNT_TEXT', { context: documentsCount });
    return (
      <div className="DocumentSelectionModal__selected">
        {documentsCount > 0 && (
          <span className="DocumentSelectionModal__selected-count">{documentsCount}</span>
        )}
        <span className="DocumentSelectionModal__selected-title">
          {titleTextForDocumentsSelectedCount}
        </span>
      </div>
    );
  }

  renderUploadFromComputerTrigger() {
    const { acceptFileTypes, allowUploadMultipleFiles } = this.props;

    const textUploadFromComputer = this.props[TSymbolProp].translate('UPLOAD_FROM_COMPUTER');
    const textUploadAccept = this.getUploadAcceptText();

    return (
      <div className="DocumentSelectionModal__footer-upload">
        <div
          className="DocumentSelectionModal__footer-upload-text"
          onClick={() => this._handleUploadFromComputerClick()}
        >
          <SvgIcon
            className="DocumentSelectionModal__footer-upload-icon"
            width="10px"
            height="11px"
          >
            {uploadFromComputerIcon}
          </SvgIcon>
          {textUploadFromComputer}
        </div>
        <div className="DocumentSelectionModal__footer-upload-accept">{textUploadAccept}</div>
        <input
          type="file"
          multiple={allowUploadMultipleFiles}
          accept={acceptFileTypes}
          onChange={() => {
            this._handleUploadFiles();
          }}
          style={{ display: 'none' }}
          ref={input => {
            this.fileInput = input;
          }}
        />
      </div>
    );
  }

  renderFooter() {
    const {
      closeModal,
      isSaving,
      allowUploadFiles,
      saveButtonText,
      cancelButtonText,
      multi
    } = this.props;
    const { selectedDocuments } = this.state;

    const textCancel = cancelButtonText || this.props[TSymbolProp].translate('CANCEL_BUTTON');
    const textButtonSave = saveButtonText || this.props[TSymbolProp].translate('SAVE_BUTTON');
    const textButtonSaving = this.props[TSymbolProp].translate('SAVING_BUTTON');

    const cancelBtnClasses = classNames({
      disabled: isSaving
    });

    const footerClasses = classNames('DocumentSelectionModal__footer', {
      'DocumentSelectionModal__footer--allow-upload': allowUploadFiles,
      'DocumentSelectionModal__footer--multi-select': multi
    });

    return (
      <div className={footerClasses}>
        {multi && this.renderSelectedCount()}
        {this.isAllowedUploadFiles() ? this.renderUploadFromComputerTrigger() : null}
        <div className="DocumentSelectionModal__footer-buttons">
          <Button
            styleType={Button.StyleType.LINK_SECONDARY}
            className={cancelBtnClasses}
            onClick={closeModal}
            disabled={isSaving}
          >
            {textCancel}
          </Button>
          <SaveButton
            onSave={() => this._handleSaveClick()}
            disabled={!selectedDocuments || selectedDocuments.length === 0}
            saveText={textButtonSave}
            savingText={textButtonSaving}
            isSaving={isSaving}
          />
        </div>
      </div>
    );
  }

  isAllowedUploadFiles = () => {
    const { allowUploadFiles, allowUploadMultipleFiles } = this.props;
    if (allowUploadFiles === false) {
      return false;
    }
    return allowUploadFiles || allowUploadMultipleFiles;
  };

  _onChoosingDocuments() {
    const { onChoosingDocument, onChoosingDocuments, multi } = this.props;
    const { selectedDocuments } = this.state;
    if (!multi) {
      const selectedNode = selectedDocuments && selectedDocuments[0];
      return selectedNode && onChoosingDocument(selectedNode.id, selectedNode.originalNode);
    } else {
      return onChoosingDocuments(selectedDocuments);
    }
  }

  _handleSaveClick() {
    const res = this._onChoosingDocuments();
    if (res && typeof res.then === 'function') {
      this.setState({ isSaving: true });
      return res.then(this.onFinish).catch(this.onFinish);
    }
    return res;
  }

  /**
   * render the modal
   * @returns {XML}
   */
  render() {
    const {
      isModalOpen,
      closeModal,
      container,
      isSaving,
      className,
      modalProps,
      title,
      backdrop,
      ...documentSelectionTreeProps
    } = this.props;

    const textTitle = title || this.props[TSymbolProp].translate('MODAL_TITLE');
    const classes = classNames('DocumentSelectionModal', className);

    return (
      <SimpleModal
        title={textTitle}
        className={classes}
        container={container}
        onExited={closeModal}
        disableModalExit={isSaving}
        show={isModalOpen}
        modalProps={modalProps}
        backdrop={backdrop}
        footer={this.renderFooter()}
      >
        <DocumentSelectionTree
          onChange={this._handleDocumentsSelect}
          {...documentSelectionTreeProps}
        />
      </SimpleModal>
    );
  }
}

DocumentSelectionModal.defaultProps = {
  treeContainerHeight: 280
};

DocumentSelectionModal.propTypes = {
  /**
   * Is modal open
   */
  isModalOpen: PropTypes.bool.isRequired,
  /**
   * Close the modal
   */
  closeModal: PropTypes.func.isRequired,
  /**
   * Callback that is called when user selects a document to open.
   * Will be called with selected document URN and document node received from `getFolderContents`. Can return a promise that would change it's isSaving state.
   */
  onChoosingDocument: PropTypes.func.isRequired,
  /**
   * Callback that is called when user selects a documents to open.
   * Will be called with selected documents nodes collection received from `getFolderContents`. Can return a promise that would change it's isSaving state.
   */
  onChoosingDocuments: PropTypes.func,
  /**
   * Whether or not allow file upload from computer.
   */
  allowUploadFiles: PropTypes.bool,
  /**
   * Whether or not allow upload multiple files from computer.
   */
  allowUploadMultipleFiles: PropTypes.bool,
  /**
   * Permit multi selection on leaf nodes
   */
  multi: PropTypes.bool,
  /**
   * Handles file upload.
   */
  uploadFiles: PropTypes.func,
  /**
   * Modal container
   */
  container: PropTypes.object,
  /**
   * Accept file types ('.jpeg, .jpg, .png, .pdf' etc.)
   */
  acceptFileTypes: PropTypes.string,
  /**
   * Method that returns a promise that gets root project folders from Docs.
   */
  getRootFolders: PropTypes.func.isRequired,
  /**
   * Method that returns a promise that gets folder's children (both folders & documents).
   */
  getFolderContents: PropTypes.func.isRequired,
  /**
   * An optional function that returns a boolean indicating whether a folder has more contents (for pagination).
   * The function is of the form:
   * ({ folder, availableChildNodes }) => bool
   *
   * folder is the parent folder object and availableChildNodes contains only the nodes fetched so far for the current folder.
   * This would allow you to determine if a folder has more contents or not based on what the user fetched so far.
   */
  hasMoreFolderContents: PropTypes.func,
  /**
   * In case each folder already contains the data of whether or not it still has more data to load and you don't want implement it using the function above,
   * You can pass this prop to indicate where in each folder the that value exists.
   */
  hasMoreFolderContentsKey: PropTypes.string,
  /**
   * Ensures that only the hasMoreFolderContents function is used to check whether there's more content to fetch (see onLoadMore in Tree.js)
   */
  dontOverrideHasMoreFolderContents: PropTypes.bool,
  /**
   * Whether the modal is in saving state.
   */
  isSaving: PropTypes.bool.isRequired,
  /**
   * A custom className for the container element of the component
   */
  className: PropTypes.string,
  /**
   * Save button's label. Default is 'open'.
   */
  saveButtonText: PropTypes.string,
  /**
   * Cancel button's label. Default is 'cancel'
   */
  cancelButtonText: PropTypes.string,
  /**
   * Locale of the component
   */
  locale: PropTypes.string,
  /**
   * Disabled node IDs (only in multi selection mode)
   */
  disabledNodesIds: PropTypes.array,
  /**
   * Determines the backdrop of modal, reference SimpleModal's backdrop props.
   */
  backdrop: SimpleModal.propTypes.backdrop,
  /**
   * Allows to pass props directly to the Modal component, reference SimpleModal's modalProps props.
   */
  modalProps: PropTypes.object,
  /**
   * Text for the modal title
   */
  title: PropTypes.node,
  /**
   * Height for the Body part of Document selection modal in which tree is populated
   */
  treeContainerHeight: PropTypes.number
};

export default withLocale(
  DocumentSelectionModal,
  ({ locale }) => new MDText(require(`./i18n/${locale}.json`))
);
